// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
import { supabase } from "@/integrations/supabase/client";
import { stableUuid, isUuid } from "./id";
import { RESTORE_ORDER, specFor, type TableSpec } from "./schema";
import type {
  ClinicalOSBackupFile,
  EntityPlan,
  RestoreMode,
  RestoreOutcome,
  RestorePlan,
  RestoreProgress,
} from "./types";

const PAGE = 1000;
const UPSERT_BATCH = 100;

type Row = Record<string, any>;
type Action = "create" | "preserve" | "update" | "skip";

interface PreparedRow {
  sourceId: string | null;
  row: Row;
  action: Action;
  reason?: string;
}

interface PreparedTable {
  spec: TableSpec;
  rows: PreparedRow[];
  plan: EntityPlan;
}

interface PreparedRestore {
  tables: PreparedTable[];
  warnings: string[];
}

const REQUIRED_REFS: Record<string, string[]> = {
  substance_formulations: ["substance_id"],
  patient_medications: ["patient_id"],
  clinical_sessions: ["patient_id"],
  medication_dose_logs: ["patient_id"],
  session_checkins: ["patient_id"],
  patient_target_symptoms: ["patient_id"],
  symptom_measurements: ["patient_id"],
  patient_vital_signs: ["patient_id"],
  meal_events: ["patient_id"],
  sleep_periods: ["patient_id"],
  substance_use_logs: ["patient_id"],
  medication_inventory: ["patient_id"],
  medication_inventory_movements: ["inventory_id"],
  patient_psylog_events: ["patient_id"],
  patient_substance_response_profiles: ["patient_id"],
  diagnostic_hypotheses: ["patient_id"],
  diagnostic_hypothesis_criteria: ["hypothesis_id"],
  diagnostic_hypothesis_symptom_links: ["hypothesis_id", "target_symptom_id"],
  diagnostic_hypothesis_revisions: ["hypothesis_id"],
  clinical_interactions: ["patient_id"],
  pharmacology_interaction_assessments: ["patient_id"],
  substance_clinical_annotations: ["substance_id"],
  clinical_effect_forecasts: ["patient_id"],
  clinical_reports: ["patient_id"],
  clinical_ai_analyses: ["patient_id"],
  curve_results: ["patient_id"],
  axis_audits: ["patient_id"],
};

function normalize(value: unknown): string {
  return String(value ?? "").trim().toLocaleLowerCase("pt-BR");
}

function cloneRows(rows: Row[] | undefined): Row[] {
  return (rows ?? []).map((row) => ({ ...row }));
}

function stripUndefined(row: Row): Row {
  const out: Row = {};
  for (const [key, value] of Object.entries(row)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

function tableRows(backup: ClinicalOSBackupFile, table: string): Row[] {
  const value = backup.data.tables?.[table];
  return Array.isArray(value) ? cloneRows(value) : [];
}

function aliasValues(row: Row): string[] {
  return [
    row.name,
    row.generic_name,
    ...(Array.isArray(row.synonyms) ? row.synonyms : []),
    ...(Array.isArray(row.brand_names) ? row.brand_names : []),
    ...(Array.isArray(row.reference_names) ? row.reference_names : []),
    ...(Array.isArray(row.brands) ? row.brands : []),
  ]
    .map(normalize)
    .filter(Boolean);
}

async function fetchPaged(table: string, select: string, decorate?: (query: any) => any): Promise<Row[]> {
  const all: Row[] = [];
  for (let from = 0; ; from += PAGE) {
    let query = (supabase as any).from(table).select(select).range(from, from + PAGE - 1);
    if (decorate) query = decorate(query);
    const { data, error } = await query;
    if (error) throw new Error(`${table}: ${error.message}`);
    const batch = (data ?? []) as Row[];
    all.push(...batch);
    if (batch.length < PAGE) break;
  }
  return all;
}

async function loadVisibleSubstances(userId: string): Promise<Row[]> {
  const rows = await fetchPaged(
    "substances",
    "id,owner_id,is_global,name,generic_name,synonyms,brand_names,reference_names,canonical_substance_id",
  );
  return rows.filter((row) => row.is_global === true || row.owner_id === userId);
}

async function loadVisibleFormulations(userId: string): Promise<Row[]> {
  try {
    const rows = await fetchPaged(
      "substance_formulations",
      "id,owner_id,is_global,substance_id,formulation_name,route",
    );
    return rows.filter((row) => row.is_global === true || row.owner_id === userId);
  } catch {
    // Em instalações antigas, a política pode ocultar formulações globais. As privadas continuam resolvíveis depois.
    return fetchPaged(
      "substance_formulations",
      "id,owner_id,is_global,substance_id,formulation_name,route",
      (q) => q.eq("owner_id", userId),
    );
  }
}

function buildSubstanceAliasMap(rows: Row[]): Map<string, Row> {
  const map = new Map<string, Row>();
  for (const row of rows) {
    for (const alias of aliasValues(row)) {
      const current = map.get(alias);
      // Preferir canônico global quando há colisão de nome; caso contrário manter o primeiro.
      if (!current || (row.is_global === true && current.is_global !== true)) map.set(alias, row);
    }
  }
  return map;
}

function formulationKey(substanceId: unknown, name: unknown, route: unknown): string {
  return `${String(substanceId ?? "")}|${normalize(name)}|${normalize(route)}`;
}

function buildFormulationMap(rows: Row[]): Map<string, Row> {
  const map = new Map<string, Row>();
  for (const row of rows) {
    map.set(formulationKey(row.substance_id, row.formulation_name, row.route), row);
  }
  return map;
}

function ensureUuidId(table: string, sourceId: unknown, row: Row): string {
  if (isUuid(sourceId)) return sourceId;
  const seed = [
    table,
    sourceId,
    row.name,
    row.full_name,
    row.session_at,
    row.actual_time,
    row.event_at,
    row.created_at,
  ]
    .map((v) => String(v ?? ""))
    .join("|");
  return stableUuid(`clinicalos:${table}`, seed);
}

function columnsForExisting(spec: TableSpec): string {
  const columns = new Set<string>([spec.pk, "owner_id"]);
  for (const key of spec.keyColumns ?? []) columns.add(key);
  for (const key of Object.keys(spec.refs ?? {})) columns.add(key);
  if (spec.table === "app_settings") {
    columns.add("preferences");
    columns.add("theme");
  }
  return Array.from(columns).join(",");
}

async function loadExistingForTable(spec: TableSpec, userId: string): Promise<Row[]> {
  if (spec.table === "substances") {
    const rows = await fetchPaged(spec.table, columnsForExisting(spec));
    return rows.filter((row) => row.owner_id === userId && row.is_global !== true);
  }
  if (spec.table === "substance_formulations") {
    const rows = await fetchPaged(spec.table, columnsForExisting(spec));
    return rows.filter((row) => row.owner_id === userId && row.is_global !== true);
  }
  return fetchPaged(spec.table, columnsForExisting(spec), (q) => q.eq("owner_id", userId));
}

function resolveReference(
  rawValue: unknown,
  targetTable: string,
  idMaps: Map<string, Map<string, string>>,
): string | null {
  if (!rawValue) return null;
  const source = String(rawValue);
  return idMaps.get(targetTable)?.get(source) ?? (isUuid(source) ? source : null);
}

function remapRow(
  source: Row,
  spec: TableSpec,
  userId: string,
  idMaps: Map<string, Map<string, string>>,
): { row: Row; missingRequired: string[] } {
  const row = { ...source, owner_id: userId };
  const missingRequired: string[] = [];

  if (spec.pk === "owner_id") row.owner_id = userId;
  else row.id = ensureUuidId(spec.table, source.id, source);

  for (const [column, target] of Object.entries(spec.refs ?? {})) {
    const original = source[column];
    if (!original) continue;
    const mapped = resolveReference(original, target, idMaps);
    if (mapped) row[column] = mapped;
    else {
      row[column] = null;
      if ((REQUIRED_REFS[spec.table] ?? []).includes(column)) missingRequired.push(column);
    }
  }

  if (spec.table === "substances") {
    row.owner_id = userId;
    row.is_global = false;
  }
  if (spec.table === "substance_formulations") {
    row.owner_id = userId;
    row.is_global = false;
  }

  return { row: stripUndefined(row), missingRequired };
}

async function prepareRestore(
  backup: ClinicalOSBackupFile,
  userId: string,
  mode: RestoreMode,
  onProgress?: (progress: RestoreProgress) => void,
): Promise<PreparedRestore> {
  const warnings: string[] = [];
  const workingTables: Record<string, Row[]> = {};
  for (const table of Object.keys(backup.data.tables ?? {})) {
    const spec = specFor(table);
    if (!spec || spec.restoreDisabled) continue;
    workingTables[table] = tableRows(backup, table);
  }

  const privateSubstanceIds = new Set((workingTables.substances ?? []).map((row) => String(row.id ?? "")).filter(Boolean));
  const privateFormulationIds = new Set((workingTables.substance_formulations ?? []).map((row) => String(row.id ?? "")).filter(Boolean));
  const idMaps = new Map<string, Map<string, string>>();
  const substanceIdMap = new Map<string, string>();
  const formulationIdMap = new Map<string, string>();
  idMaps.set("substances", substanceIdMap);
  idMaps.set("substance_formulations", formulationIdMap);

  onProgress?.({ phase: "Resolvendo substâncias" });
  const visibleSubstances = await loadVisibleSubstances(userId);
  const bySubstanceId = new Map(visibleSubstances.map((row) => [String(row.id), row]));
  const byAlias = buildSubstanceAliasMap(visibleSubstances);

  // Referências sem registro privado: reaproveitar canônico global/privado atual; se não existir, criar placeholder privado revisável.
  const syntheticSubstances: Row[] = [];
  for (const ref of backup.data.substanceRefs ?? []) {
    const sourceId = String(ref.id ?? "");
    if (!sourceId || privateSubstanceIds.has(sourceId)) continue;
    const byId = bySubstanceId.get(sourceId) ?? (ref.canonical_substance_id ? bySubstanceId.get(ref.canonical_substance_id) : undefined);
    const byName = byAlias.get(normalize(ref.name));
    const match = byId ?? byName;
    if (match) {
      substanceIdMap.set(sourceId, String(match.id));
      continue;
    }
    const id = ensureUuidId("substances", sourceId, { name: ref.name });
    substanceIdMap.set(sourceId, id);
    syntheticSubstances.push({
      id,
      owner_id: userId,
      is_global: false,
      name: ref.name || "Substância importada",
      synonyms: ref.synonyms ?? [],
      requires_clinical_review: true,
      review_status: "não revisada",
      source_notes: "Criada automaticamente durante restauração para preservar uma referência não encontrada no catálogo atual.",
    });
    warnings.push(`Substância "${ref.name}" não encontrada no catálogo atual; será criada como cópia privada em revisão.`);
  }
  if (syntheticSubstances.length) workingTables.substances = [...(workingTables.substances ?? []), ...syntheticSubstances];

  // Registros privados de substância: mapear apenas para cópias privadas do mesmo usuário, nunca sobrescrever canônicos globais.
  const privateVisibleById = new Map(visibleSubstances.filter((row) => row.owner_id === userId && row.is_global !== true).map((row) => [String(row.id), row]));
  const privateVisibleByName = new Map(visibleSubstances
    .filter((row) => row.owner_id === userId && row.is_global !== true)
    .map((row) => [normalize(row.name ?? row.generic_name), row]));
  for (const row of workingTables.substances ?? []) {
    const sourceId = String(row.id ?? "");
    const match = privateVisibleById.get(sourceId) ?? privateVisibleByName.get(normalize(row.name ?? row.generic_name));
    const desiredId = match ? String(match.id) : ensureUuidId("substances", sourceId, row);
    if (sourceId) substanceIdMap.set(sourceId, desiredId);
    row.id = desiredId;
  }

  onProgress?.({ phase: "Resolvendo formulações" });
  const visibleFormulations = await loadVisibleFormulations(userId);
  const byFormulationId = new Map(visibleFormulations.map((row) => [String(row.id), row]));
  const byFormulationKey = buildFormulationMap(visibleFormulations);
  const syntheticFormulations: Row[] = [];
  for (const ref of backup.data.formulationRefs ?? []) {
    const sourceId = String(ref.id ?? "");
    if (!sourceId || privateFormulationIds.has(sourceId)) continue;
    const mappedSubstance = ref.substance_id ? resolveReference(ref.substance_id, "substances", idMaps) : null;
    const byId = byFormulationId.get(sourceId);
    const byKey = mappedSubstance
      ? byFormulationKey.get(formulationKey(mappedSubstance, ref.formulation_name, ref.route))
      : undefined;
    const match = byId ?? byKey;
    if (match) {
      formulationIdMap.set(sourceId, String(match.id));
      continue;
    }
    if (!mappedSubstance || !ref.formulation_name) continue;
    const id = ensureUuidId("substance_formulations", sourceId, { formulation_name: ref.formulation_name, route: ref.route });
    formulationIdMap.set(sourceId, id);
    syntheticFormulations.push({
      id,
      owner_id: userId,
      substance_id: mappedSubstance,
      formulation_name: ref.formulation_name,
      route: ref.route,
      is_global: false,
      notes: "Formulação mínima criada automaticamente durante restauração; revisar parâmetros PK/PD antes de uso analítico.",
    });
    warnings.push(`Formulação "${ref.formulation_name}" não encontrada; será criada como privada sem inventar parâmetros PK/PD.`);
  }
  if (syntheticFormulations.length) {
    workingTables.substance_formulations = [...(workingTables.substance_formulations ?? []), ...syntheticFormulations];
  }

  // Pré-mapear ids das formulações privadas para que referências filhas sejam reescritas mesmo antes do upsert.
  for (const row of workingTables.substance_formulations ?? []) {
    const sourceId = String(row.id ?? "");
    const mappedSubstance = row.substance_id ? resolveReference(row.substance_id, "substances", idMaps) : null;
    const match = mappedSubstance
      ? byFormulationKey.get(formulationKey(mappedSubstance, row.formulation_name, row.route))
      : undefined;
    const desiredId = match && match.owner_id === userId && match.is_global !== true
      ? String(match.id)
      : ensureUuidId("substance_formulations", sourceId, row);
    if (sourceId) formulationIdMap.set(sourceId, desiredId);
    row.id = desiredId;
    if (mappedSubstance) row.substance_id = mappedSubstance;
  }

  const preparedTables: PreparedTable[] = [];
  const ordered = RESTORE_ORDER.filter((table) => workingTables[table]?.length);

  for (let tableIndex = 0; tableIndex < ordered.length; tableIndex += 1) {
    const table = ordered[tableIndex];
    const spec = specFor(table)!;
    onProgress?.({ phase: `Analisando ${spec.label}`, current: tableIndex + 1, total: ordered.length });

    let existingRows: Row[] = [];
    try {
      existingRows = await loadExistingForTable(spec, userId);
    } catch (error: any) {
      warnings.push(`Não foi possível analisar ${spec.label}: ${error?.message ?? "erro de leitura"}. A tabela será ignorada.`);
      const skippedRows = (workingTables[table] ?? []).map((row) => ({
        sourceId: row.id ? String(row.id) : null,
        row,
        action: "skip" as const,
        reason: "Falha ao ler tabela de destino.",
      }));
      preparedTables.push({
        spec,
        rows: skippedRows,
        plan: {
          table,
          label: spec.label,
          group: spec.group,
          total: skippedRows.length,
          toCreate: 0,
          toPreserve: 0,
          toUpdate: 0,
          skipped: skippedRows.length,
        },
      });
      continue;
    }

    const byPk = new Map<string, Row>();
    const byNatural = new Map<string, Row>();
    for (const row of existingRows) {
      const pk = row[spec.pk];
      if (pk) byPk.set(String(pk), row);
      const natural = spec.naturalKey?.(row);
      if (natural) byNatural.set(natural, row);
    }

    const tableMap = idMaps.get(table) ?? new Map<string, string>();
    idMaps.set(table, tableMap);
    const seenNatural = new Set<string>();
    const seenPk = new Set<string>();
    const preparedRows: PreparedRow[] = [];

    for (const source of workingTables[table] ?? []) {
      const sourceId = source[spec.pk] ? String(source[spec.pk]) : null;
      const remapped = remapRow(source, spec, userId, idMaps);
      if (remapped.missingRequired.length) {
        preparedRows.push({
          sourceId,
          row: remapped.row,
          action: "skip",
          reason: `Referência obrigatória ausente: ${remapped.missingRequired.join(", ")}`,
        });
        continue;
      }

      const row = remapped.row;
      const pk = String(row[spec.pk] ?? "");
      const natural = spec.naturalKey?.(row) ?? null;
      if ((pk && seenPk.has(pk)) || (natural && seenNatural.has(natural))) {
        preparedRows.push({ sourceId, row, action: "skip", reason: "Duplicado dentro do próprio arquivo." });
        continue;
      }
      if (pk) seenPk.add(pk);
      if (natural) seenNatural.add(natural);

      const existing = (pk ? byPk.get(pk) : undefined) ?? (natural ? byNatural.get(natural) : undefined);
      if (existing) {
        const existingPk = String(existing[spec.pk]);
        if (spec.pk === "id") row.id = existingPk;
        else row.owner_id = userId;
        if (sourceId) tableMap.set(sourceId, existingPk);

        // Preferências do Piru devem ser mescladas, não apagar preferências atuais do ClinicalOS.
        const mergePiruSettings = table === "app_settings" && backup.exportedBy === "piru-import";
        if (mergePiruSettings) {
          row.preferences = { ...(existing.preferences ?? {}), ...(row.preferences ?? {}) };
          row.theme = existing.theme ?? row.theme;
        }

        preparedRows.push({
          sourceId,
          row,
          action: mergePiruSettings || mode === "update" ? "update" : "preserve",
        });
      } else {
        if (sourceId) tableMap.set(sourceId, String(row[spec.pk]));
        preparedRows.push({ sourceId, row, action: "create" });
      }
    }

    const plan: EntityPlan = {
      table,
      label: spec.label,
      group: spec.group,
      total: preparedRows.length,
      toCreate: preparedRows.filter((r) => r.action === "create").length,
      toPreserve: preparedRows.filter((r) => r.action === "preserve").length,
      toUpdate: preparedRows.filter((r) => r.action === "update").length,
      skipped: preparedRows.filter((r) => r.action === "skip").length,
    };
    preparedTables.push({ spec, rows: preparedRows, plan });
  }

  return { tables: preparedTables, warnings };
}

export async function previewRestore(
  backup: ClinicalOSBackupFile,
  userId: string,
  mode: RestoreMode,
  onProgress?: (progress: RestoreProgress) => void,
): Promise<RestorePlan> {
  const prepared = await prepareRestore(backup, userId, mode, onProgress);
  const entities = prepared.tables.map((table) => table.plan);
  return {
    entities,
    total: entities.reduce((sum, e) => sum + e.total, 0),
    toCreate: entities.reduce((sum, e) => sum + e.toCreate, 0),
    toPreserve: entities.reduce((sum, e) => sum + e.toPreserve, 0),
    toUpdate: entities.reduce((sum, e) => sum + e.toUpdate, 0),
    skipped: entities.reduce((sum, e) => sum + e.skipped, 0),
    warnings: prepared.warnings,
  };
}

async function upsertRows(
  table: string,
  pk: string,
  rows: Row[],
): Promise<{ ok: number; errors: string[] }> {
  let ok = 0;
  const errors: string[] = [];
  for (let i = 0; i < rows.length; i += UPSERT_BATCH) {
    const batch = rows.slice(i, i + UPSERT_BATCH);
    const { error } = await (supabase as any).from(table).upsert(batch, { onConflict: pk });
    if (!error) {
      ok += batch.length;
      continue;
    }
    // Isolar erro por linha para que um registro inválido não derrube todo o lote.
    for (const row of batch) {
      const single = await (supabase as any).from(table).upsert(row, { onConflict: pk });
      if (single.error) errors.push(single.error.message);
      else ok += 1;
    }
  }
  return { ok, errors: Array.from(new Set(errors)) };
}

export async function restoreBackup(
  backup: ClinicalOSBackupFile,
  userId: string,
  mode: RestoreMode,
  onProgress?: (progress: RestoreProgress) => void,
): Promise<RestoreOutcome> {
  const prepared = await prepareRestore(backup, userId, mode, onProgress);
  const outcome: RestoreOutcome = {
    batchId: null,
    created: {},
    preserved: {},
    updated: {},
    skipped: {},
    errors: [],
    warnings: [...prepared.warnings],
  };

  let completed = 0;
  const total = prepared.tables.length;
  for (const table of prepared.tables) {
    completed += 1;
    onProgress?.({ phase: `Restaurando ${table.spec.label}`, current: completed, total });
    outcome.preserved[table.spec.table] = table.rows.filter((r) => r.action === "preserve").length;
    outcome.skipped[table.spec.table] = table.rows.filter((r) => r.action === "skip").length;

    const creates = table.rows.filter((r) => r.action === "create").map((r) => r.row);
    const updates = table.rows.filter((r) => r.action === "update").map((r) => r.row);
    if (creates.length) {
      const result = await upsertRows(table.spec.table, table.spec.pk, creates);
      outcome.created[table.spec.table] = result.ok;
      for (const message of result.errors) outcome.errors.push({ table: table.spec.table, message });
    }
    if (updates.length) {
      const result = await upsertRows(table.spec.table, table.spec.pk, updates);
      outcome.updated[table.spec.table] = result.ok;
      for (const message of result.errors) outcome.errors.push({ table: table.spec.table, message });
    }
  }

  try {
    await (supabase as any).from("audit_logs").insert({
      owner_id: userId,
      entity: "backup",
      action: "restore_v1",
      diff: {
        format: backup.format,
        format_version: backup.formatVersion,
        source_app: backup.appVersion ?? backup.app,
        exported_at: backup.exportedAt,
        mode,
        created: outcome.created,
        preserved: outcome.preserved,
        updated: outcome.updated,
        skipped: outcome.skipped,
        error_count: outcome.errors.length,
      },
    });
  } catch {
    // Auditoria não deve invalidar uma restauração já concluída.
  }

  return outcome;
}
